/* eslint-disable camelcase */
import {
  all, call, fork, put, select, takeLatest,
} from 'redux-saga/effects';
import { addSeconds, compareDesc } from 'date-fns';
import {
  appAuthReset, appMounted, appSignOut, fetchTranslations, validateAuthToken,
} from '../actions/app-actions';
import {
  fetchTranslationsApi, fetchUserAccessTagsApi, requestAppSignOutApi, fetchEsignCreditsApi,
  fetchUserImpersonatorsApi, validateAuthTokenApi, fetchLoginAccessTagsApi, fetchFeatureConfigApi,
  fetchEntitlementsApi, fetchJWTTokenApi, fetchWorkflowBuilderConfigApi,
} from '../apis';
import {
  resetUser, invalidateUser, updateUserLoaded, authenticateUser, updateAccessMap,
  updateUserSigningOut, updateUser,
} from '../reducers/user-reducer';
import { updateTranslations } from '../reducers/translations-reducer';
import LOCALES from '../../constants/locales';
import AuthManager, { AccessKeys, FeatureKeys } from '../../store/auth-manager';
import {
  updateImpersonator, updateImpersonatorList, resetImpersonatorReducer,
} from '../reducers/impersonate-user-reducer';
import {
  clearSessionStorage, getFromSessionStorage, getUserObjectFromSessionStorage, setInSessionStorage,
  hasTenantAndAuthUser,
} from '../../utils/session-storage-utils';
import {
  ACTIVE_IMPERSONATOR, AUTH_TOKEN, CURRENT_USER, JWT_TOKEN, JWT_TOKEN_EXPIRY, SSO_LOGOUT_URL,
} from '../../constants/session-storage-keys';
import {
  failureFetchEsignCredits, successFetchEsignCredits,
} from '../reducers/esign-credits-reducer';
import { updateFeatureConfig } from '../reducers/feature-config-reducer';
import {
  fetchWorkflowBuilderConfigSuccess,
  fetchWorkflowBuilderConfigFailure,
} from '../reducers/workflow-builder-config-reducer';
import notification from '../../components/views/notification/notification';
import { selectUserDetails } from '../selectors';
import { convertArrayToObject } from '../../utils/shape-utils';

function* fetchTranslationsSaga({ payload }) {
  const { response } = yield call(fetchTranslationsApi, payload);
  const { status, data } = response;

  if (status) {
    yield put(updateTranslations(data));
  }
}

function* fetchEntitlementsSaga() {
  const { response } = yield call(fetchEntitlementsApi);
  const { messages, data } = response;

  if (data) {
    const { entitlements, products = [] } = data;
    const { accessMap } = yield select(selectUserDetails);
    const entitlementMap = convertArrayToObject(entitlements);
    const entitlementAccesses = AuthManager.getAccessMapFromEntitlementMap(entitlementMap);
    yield put(updateAccessMap({ ...accessMap, ...entitlementAccesses }));
    yield put(updateUser({ products }));
  } else {
    notification.error(messages[0].message);
  }
}

function* fetchJWTTokenSaga() {
  const { response } = yield call(fetchJWTTokenApi);

  if (response && response.access_token) {
    const { access_token, token_type, expires_in } = response;
    const expiryDate = addSeconds(new Date(), expires_in);
    setInSessionStorage(JWT_TOKEN, `${token_type} ${access_token}`);
    setInSessionStorage(JWT_TOKEN_EXPIRY, expiryDate.getTime());
    yield call(fetchEntitlementsSaga);
  }
}

function* fetchWorkflowBuilderConfig() {
  const { response } = yield call(fetchWorkflowBuilderConfigApi);
  const { status, data } = response;

  if (status) {
    yield put(fetchWorkflowBuilderConfigSuccess(data));
  } else {
    yield put(fetchWorkflowBuilderConfigFailure());
  }
}

function* fetchUserAccessTags() {
  // This api call provide user role accessTags
  const { response } = yield call(fetchLoginAccessTagsApi);
  const { status, data } = response;

  if (status) {
    // This api call provide user feature access permissions
    const accessTagsRes = yield call(fetchUserAccessTagsApi);
    const { status: accessTagsStatus, data: accessTagsData } = accessTagsRes.response;

    if (accessTagsStatus) {
      // The reduce converts an array of accessTags into an object where
      // each tag is a key with the value true.
      // Then result is combined with accessTagsData to form the final accessTags object.
      let accessTags = data.accessTags.reduce((acc, tag) => ({
        ...acc,
        [tag]: true,
      }), {});
      accessTags = {
        ...accessTags,
        ...accessTagsData,
      };
      const currentUser = getUserObjectFromSessionStorage();
      currentUser.accessTags = data.accessTags;
      setInSessionStorage(CURRENT_USER, currentUser);
      const updatedAccessMap = AuthManager.getAccessMapFromTags(accessTags);
      yield put(updateAccessMap(updatedAccessMap));

      if (hasTenantAndAuthUser()) {
        if (!getFromSessionStorage(JWT_TOKEN)) {
          yield call(fetchJWTTokenSaga);
        } else {
          yield call(fetchEntitlementsSaga);
        }
      }
    }
  }
}

function* fetchEsignCredits() {
  const { response } = yield call(fetchEsignCreditsApi);
  const { status, data } = response;

  if (status) {
    yield put(successFetchEsignCredits(data));
  } else {
    yield put(failureFetchEsignCredits());
    notification.error('MSG_TEXT_SOMETHING_WENT_WRONG', 'MSG_HELPER_SOMETHING_WENT_WRONG');
  }
}

function* fetchUserImpersonators() {
  const { response } = yield call(fetchUserImpersonatorsApi);
  const { status, data } = response;
  if (status) {
    yield put(updateImpersonatorList(data?.filters || []));
  }
}

function* fetchFeatureConfig() {
  const { response } = yield call(fetchFeatureConfigApi);
  const { status, messages, data } = response;

  if (status) {
    yield put(updateFeatureConfig(data));
  } else {
    notification.error(messages[0].message);
  }
}

function* fetchUser() {
  yield put(updateUserLoaded(false));

  if (getFromSessionStorage(AUTH_TOKEN)) {
    yield put(authenticateUser());
    yield call(fetchUserAccessTags);
    const { accessMap } = yield select(selectUserDetails);
    if (accessMap?.[AccessKeys.WORKFLOW_V4_ACCESS]
      && AuthManager.isAuthorised(FeatureKeys.WORKFLOW_BUILDER_CONFIG)) {
      yield fork(fetchWorkflowBuilderConfig);
    }
    yield call(fetchFeatureConfig);
    // Persist active user on page reload.
    yield put(updateImpersonator(getFromSessionStorage(ACTIVE_IMPERSONATOR)));
    yield call(fetchUserImpersonators);
    // TODO remove once esign credit api is deployed on production
    if (!window.location.pathname.includes('pdf-editor')) {
      yield call(fetchEsignCredits);
    }
  } else {
    clearSessionStorage();
    yield put(resetUser());
    yield put(invalidateUser());
    yield put(resetImpersonatorReducer());
  }
  yield put(updateUserLoaded(true));
}

function* fetchAppMountedData() {
  yield call(fetchUser);
  yield fork(fetchTranslationsSaga, { payload: LOCALES.EN_US });
}

function* fetchAppAuthData() {
  yield call(fetchUser);
}

function* requestAppSignOut() {
  // Get SSO logout URL before clearing session storage
  const ssoLogoutUrl = getFromSessionStorage(SSO_LOGOUT_URL);

  yield put(invalidateUser());
  yield put(updateUserSigningOut(true));
  yield put(resetImpersonatorReducer());
  yield call(requestAppSignOutApi);

  clearSessionStorage();
  yield put(resetUser());
  yield put(updateUserLoaded(true));

  // Redirect to SSO logout URL if user logged in via SSO
  if (ssoLogoutUrl) {
    window.location.href = ssoLogoutUrl;
  }
}

function* validateAuthTokenSaga() {
  const { response } = yield call(validateAuthTokenApi);
  const { status } = response;

  if (status) {
    yield put(authenticateUser());
    const jwtTokenExpiry = getFromSessionStorage(JWT_TOKEN_EXPIRY);
    if (hasTenantAndAuthUser()
      && compareDesc(new Date(), new Date(jwtTokenExpiry)) <= 0) {
      yield call(fetchJWTTokenSaga);
    }
  } else {
    yield put(invalidateUser());
    yield put(resetImpersonatorReducer());
  }
}

function* appMountedAction() {
  yield takeLatest(appMounted().type, fetchAppMountedData);
}

// Use this to reauthenticate the app after cookie change e.g. sign-in & sign-up
function* appAuthResetAction() {
  yield takeLatest(appAuthReset().type, fetchAppAuthData);
}

function* fetchTranslationsAction() {
  yield takeLatest(fetchTranslations().type, fetchTranslationsSaga);
}

function* requestAppSignOutAction() {
  yield takeLatest(appSignOut().type, requestAppSignOut);
}

function* validateAuthTokenAction() {
  yield takeLatest(validateAuthToken().type, validateAuthTokenSaga);
}

export default function* root() {
  yield all([
    fork(appMountedAction),
    fork(fetchTranslationsAction),
    fork(appAuthResetAction),
    fork(requestAppSignOutAction),
    fork(validateAuthTokenAction),
  ]);
}
